import React, { useMemo, useState } from "react";

// The five outcome counts. Only the two with row-level detail (unmatched SKUs,
// error files) become tabs; the rest are count-only stat pills per the API
// contract, which returns no per-item list for imported / skipped / ignored.
const STAT_PILLS = [
  { key: "imported", label: "Imported", tone: "success" },
  { key: "skipped", label: "Skipped", tone: "muted" },
  { key: "unmatched", label: "Unmatched", tone: "warning" },
  { key: "errors", label: "Errors", tone: "danger" },
  { key: "ignored", label: "Ignored", tone: "muted" },
];

const countOf = (value) => (Array.isArray(value) ? value.length : value ?? 0);

const ImportResults = ({ summary }) => {
  const tabs = useMemo(
    () =>
      [
        { key: "unmatched", label: "Unmatched", items: summary.unmatched || [] },
        { key: "errors", label: "Errors", items: summary.errors || [] },
      ].filter((tab) => tab.items.length > 0),
    [summary]
  );

  const [activeTab, setActiveTab] = useState(tabs[0]?.key ?? null);
  const [copied, setCopied] = useState(false);

  const active = tabs.find((tab) => tab.key === activeTab) ?? tabs[0];

  const copyUnmatched = async () => {
    try {
      await navigator.clipboard.writeText((summary.unmatched || []).join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — non-critical */
    }
  };

  return (
    <div className="pii-results">
      <div className="pii-stats">
        {STAT_PILLS.map(({ key, label, tone }) => (
          <div key={key} className={`pii-stat pii-stat--${tone}`}>
            <span className="pii-stat__value">{countOf(summary[key])}</span>
            <span className="pii-stat__label">{label}</span>
          </div>
        ))}
      </div>

      {tabs.length === 0 ? (
        <p className="pii-results__clean">
          Every file was processed — nothing unmatched and no errors.
        </p>
      ) : (
        <div className="pii-tabs">
          <div className="pii-tabs__nav" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active.key === tab.key}
                className={`pii-tabs__tab ${active.key === tab.key ? "is-active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                <span className="pii-tabs__count">{tab.items.length}</span>
              </button>
            ))}
          </div>

          <div className="pii-tabs__panel" role="tabpanel">
            {active.key === "unmatched" ? (
              <>
                <div className="pii-panel__head">
                  <span>
                    No live product matched these SKUs. Fix the filenames (or
                    promote the products) and re-drop the images.
                  </span>
                  <button
                    type="button"
                    className="pii-copy-btn"
                    onClick={copyUnmatched}
                  >
                    {copied ? "Copied" : "Copy all"}
                  </button>
                </div>
                <ul className="pii-list pii-list--codes">
                  {active.items.map((sku) => (
                    <li key={sku}>{sku}</li>
                  ))}
                </ul>
              </>
            ) : (
              <ul className="pii-list">
                {active.items.map((row) => (
                  <li key={row.file} className="pii-error-row">
                    <span className="pii-error-row__file">{row.file}</span>
                    <span className="pii-error-row__reason">{row.reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportResults;
